import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Folder, FileText, FileCode, FileJson, Image as ImageIcon, Search, Filter, Grid, List, Plus, MoreVertical, ChevronRight, ChevronDown, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface ContextItem {
  id: string;
  name: string;
  type: 'markdown' | 'code' | 'json' | 'image';
  size: string;
  lastModified: string;
  preview?: string;
}
interface FolderStructure {
  id: string;
  name: string;
  type: 'folder';
  items: (ContextItem | FolderStructure)[];
  count: number;
}
export function ContextStoreBrowser({
  onClose
}: {
  onClose: () => void;
}) {
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [selectedFolder, setSelectedFolder] = useState<string>('stream-context');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['stream-context']));
  const [searchQuery, setSearchQuery] = useState('');
  const toggleFolder = (id: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(id)) newExpanded.delete(id);else newExpanded.add(id);
    setExpandedFolders(newExpanded);
    setSelectedFolder(id);
  };
  // Mock Data
  const contextData: FolderStructure[] = [{
    id: 'stream-context',
    name: 'Stream Context',
    type: 'folder',
    count: 12,
    items: [{
      id: 'f1',
      name: 'artifacts',
      type: 'folder',
      count: 5,
      items: []
    }, {
      id: 'f2',
      name: 'decisions',
      type: 'folder',
      count: 3,
      items: []
    }, {
      id: 'f3',
      name: 'research',
      type: 'folder',
      count: 4,
      items: []
    }]
  }, {
    id: 'shared-context',
    name: 'Shared Context',
    type: 'folder',
    count: 8,
    items: [{
      id: 'f4',
      name: 'best-practices',
      type: 'folder',
      count: 5,
      items: []
    }, {
      id: 'f5',
      name: 'templates',
      type: 'folder',
      count: 3,
      items: []
    }]
  }];
  const currentItems: ContextItem[] = [{
    id: '1',
    name: 'dns-patterns.md',
    type: 'markdown',
    size: '2.4 KB',
    lastModified: '2h ago',
    preview: '# DNS Patterns\nCommon patterns for DNS implementation...'
  }, {
    id: '2',
    name: 'config.json',
    type: 'json',
    size: '1.1 KB',
    lastModified: '5h ago',
    preview: '{\n  "ttl": 3600,\n  "retries": 3\n}'
  }, {
    id: '3',
    name: 'architecture.png',
    type: 'image',
    size: '450 KB',
    lastModified: '1d ago'
  }, {
    id: '4',
    name: 'resolver.ts',
    type: 'code',
    size: '12 KB',
    lastModified: '3h ago',
    preview: 'export class DNSResolver {\n  constructor(config) {\n    this.config = config\n  }\n}'
  }];
  const getIcon = (type: string) => {
    switch (type) {
      case 'markdown':
        return <FileText className="w-5 h-5 text-primary-400" />;
      case 'code':
        return <FileCode className="w-5 h-5 text-warning-400" />;
      case 'json':
        return <FileJson className="w-5 h-5 text-warning-400" />;
      case 'image':
        return <ImageIcon className="w-5 h-5 text-purple-400" />;
      case 'folder':
        return <Folder className="w-5 h-5 text-primary-500" />;
      default:
        return <FileText className="w-5 h-5 text-text-secondary" />;
    }
  };
  return <motion.div initial={{
    x: '100%'
  }} animate={{
    x: 0
  }} exit={{
    x: '100%'
  }} className="fixed inset-y-0 right-0 w-[800px] bg-surface-900 border-l border-surface-600 shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-surface-600 flex items-center justify-between bg-surface-900/95 backdrop-blur">
        <h2 className="text-lg font-semibold text-text-primary">Context Store</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search context..." className="bg-surface-800 border border-surface-500 rounded-lg pl-9 pr-4 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 w-64" />
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Tree */}
        <div className="w-64 border-r border-surface-600 bg-surface-900/50 overflow-y-auto p-4">
          <div className="space-y-1">
            {contextData.map(folder => <div key={folder.id}>
                <button onClick={() => toggleFolder(folder.id)} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors ${selectedFolder === folder.id ? 'bg-primary-500/10 text-primary-400' : 'text-text-secondary hover:text-text-primary hover:bg-surface-800'}`}>
                  {expandedFolders.has(folder.id) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <Folder className="w-4 h-4" />
                  <span className="text-sm font-medium flex-1 text-left">
                    {folder.name}
                  </span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    {folder.count}
                  </Badge>
                </button>

                {expandedFolders.has(folder.id) && <div className="ml-6 mt-1 space-y-1 border-l border-surface-600 pl-2">
                    {folder.items.map((subItem) => <button key={subItem.id} onClick={() => setSelectedFolder(subItem.id)} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${selectedFolder === subItem.id ? 'bg-primary-500/10 text-primary-400' : 'text-text-secondary hover:text-text-primary hover:bg-surface-800'}`}>
                        <Folder className="w-3 h-3" />
                        <span className="flex-1 text-left">{subItem.name}</span>
                        {'count' in subItem && <span className="text-xs opacity-50">
                          {subItem.count}
                        </span>}
                      </button>)}
                  </div>}
              </div>)}
          </div>
        </div>

        {/* Right Panel - Contents */}
        <div className="flex-1 flex flex-col bg-surface-900">
          {/* Toolbar */}
          <div className="p-4 border-b border-surface-600 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" className="h-8">
                <Filter className="w-3 h-3 mr-2" />
                All Types
              </Button>
              <div className="h-4 w-px bg-surface-700 mx-2" />
              <span className="text-sm text-text-secondary">4 items</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex bg-surface-800 rounded-lg p-0.5 border border-surface-500">
                <button onClick={() => setViewMode('list')} className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-surface-700 text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}>
                  <List className="w-4 h-4" />
                </button>
                <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-surface-700 text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}>
                  <Grid className="w-4 h-4" />
                </button>
              </div>
              <Button size="sm" className="bg-primary-600 hover:bg-blue-700 text-text-primary border-none h-8">
                <Plus className="w-4 h-4 mr-2" />
                Add Context
              </Button>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-4">
            {viewMode === 'list' ? <div className="space-y-2">
                {currentItems.map(item => <motion.div key={item.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="group flex items-center gap-4 p-3 rounded-xl bg-surface-800/50 border border-surface-600 hover:border-surface-500 hover:bg-surface-800 transition-all cursor-pointer">
                    <div className="w-10 h-10 rounded-lg bg-surface-900 flex items-center justify-center border border-surface-600 group-hover:border-surface-500">
                      {getIcon(item.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-text-primary truncate">
                        {item.name}
                      </h4>
                      <div className="flex items-center gap-3 text-xs text-text-muted mt-0.5">
                        <span>{item.size}</span>
                        <span>•</span>
                        <span>{item.lastModified}</span>
                      </div>
                    </div>
                    {item.preview && <div className="hidden md:block w-64 text-xs text-text-muted truncate font-mono bg-surface-900/50 px-2 py-1 rounded border border-surface-600">
                        {item.preview}
                      </div>}
                    <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </motion.div>)}
              </div> : <div className="grid grid-cols-3 gap-4">
                {currentItems.map(item => <motion.div key={item.id} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="group p-4 rounded-xl bg-surface-800/50 border border-surface-600 hover:border-surface-500 hover:bg-surface-800 transition-all cursor-pointer flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-2xl bg-surface-900 flex items-center justify-center border border-surface-600 group-hover:border-surface-500 mb-3 shadow-sm">
                      {getIcon(item.type)}
                    </div>
                    <h4 className="text-sm font-medium text-text-primary truncate w-full mb-1">
                      {item.name}
                    </h4>
                    <p className="text-xs text-text-muted">
                      {item.size} • {item.lastModified}
                    </p>
                  </motion.div>)}
              </div>}
          </div>
        </div>
      </div>
    </motion.div>;
}